import React, { createContext, useState, useContext, useEffect, useMemo, useCallback } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { translations } from './translations';
import { DEFAULT_LANGUAGE, LANGUAGES } from './languages';

const TranslationContext = createContext({});
const ALLOWED_LANG = new Set(LANGUAGES.map((language) => language.code));

export const useTranslation = () => {
  const context = useContext(TranslationContext);
  if (!context || typeof context.t !== 'function') {
    throw new Error('useTranslation must be used within a TranslationProvider');
  }
  return context;
};

export const TranslationProvider = ({ children }) => {
  const { lang: rawLang } = useParams();
  const { search } = useLocation();
  const langParam = rawLang && ALLOWED_LANG.has(rawLang) ? rawLang : null;
  const langQuery = useMemo(() => {
    try {
      return new URLSearchParams(search).get('lang');
    } catch {
      return null;
    }
  }, [search]);
  const langFromQuery = langQuery && ALLOWED_LANG.has(langQuery) ? langQuery : null;
  /** Route `/:lang/...` wins over `?lang=` (e.g. public /tools/calculator?lang=hy). */
  const effectiveLang = useMemo(
    () => langParam || langFromQuery || DEFAULT_LANGUAGE,
    [langParam, langFromQuery]
  );

  const [currentLang, setCurrentLang] = useState(effectiveLang);
  const [isChanging, setIsChanging] = useState(false);

  useEffect(() => {
    if (effectiveLang === currentLang) return;
    setIsChanging(true);
    const timer = setTimeout(() => {
      setCurrentLang(effectiveLang);
      setTimeout(() => {
        setIsChanging(false);
      }, 300);
    }, 150);
    return () => clearTimeout(timer);
  }, [effectiveLang, currentLang]);

  const t = useCallback((key) => {
    if (key == null || typeof key !== 'string') return '';
    const keys = key.split('.').filter(Boolean);
    if (keys.length === 0) return '';

    let value = translations[currentLang];
    for (const k of keys) {
      if (value == null || typeof value !== 'object' || !(k in value)) {
        return '';
      }
      value = value[k];
    }

    return typeof value === 'string' ? value : '';
  }, [currentLang]);

  const value = useMemo(
    () => ({
      t,
      currentLang,
      isChanging,
      translations: translations[currentLang],
    }),
    [t, currentLang, isChanging]
  );

  return (
    <TranslationContext.Provider value={value}>
      {children}
    </TranslationContext.Provider>
  );
};

